import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { useSelector } from "react-redux";
import FallbackImage from "../components/FallbackImage";
import { getImageFullUrl, pageRoute, siteData } from "../utils/common";

const SignupPage = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ================= Password Validation =================
  const passwordValidation = (value) => {
    const regex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/;
    return regex.test(value);
  };

  // ================= Submit =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    let newErrors = {};

    if (!fullName) newErrors.fullName = "Full name is required";

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (!passwordValidation(password)) {
      newErrors.password =
        "Min 8 chars, include uppercase, number & special char";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.post("/api/admin/sign-up", {
        fullName,
        email,
        password,
      });

      toast.success("Signup successful! Please login.", {
        position: "top-center",
      });

      setTimeout(() => {
        navigate(pageRoute.login);
      }, 2000);
    } catch (err) {
      if (err.response) {
        toast.error(err.response.data.message || "Signup failed", {
          position: "top-center",
        });
      } else {
        toast.error("Network error. Please try again.", {
          position: "top-center",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const logo = useSelector((state) => state.logo);

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#ebebeb",
          px: 2,
          py: 2,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 900,
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Grid container>
            {/* Left Side - Logo & Branding */}
            <Grid
              item
              size={{ xs: 12, md: 5 }}

              sx={{
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                px: 3,
                textAlign: "center",
              }}
            >
              <FallbackImage
                src={'/logo.png'}
                style={{ maxHeight: "80px", marginBottom: "20px" }}
              />
              <Typography variant="h4" fontWeight="bold">
                {siteData.name}
              </Typography>
              <Typography variant="body1" mt={2} sx={{ opacity: 0.85 }}>
                Welcome to the Admin Panel.
                Sign up to manage your dashboard and access advanced tools.
              </Typography>
            </Grid>

            {/* Right Side - Signup Form */}
            <Grid item size={{ xs: 12, md: 7 }}
              sx={{ p: 4 }}>
              <h1 style={{ textAlign: 'center' }}>Create Account</h1>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Full Name"
                  margin="normal"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                />

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 1, py: 1.2, fontWeight: "bold" }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
                </Button>
              </form>

              <Typography textAlign="center" sx={{ mt: 2 }}>
                Already have an account?{" "}
                <Link to={pageRoute.login} style={{ textDecoration: "none", color: "#1976d2" }}>
                  Login
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default SignupPage;
